import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-big-calendar';

import { HttpStatusCode } from 'axios';
import { closeModal, formatDate, openModal, showToastMessage } from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { INITIAL_FORM_STATE, ITEMS_PER_PAGE, TIME_FORMAT } from 'modules/calendar/core/constant';
import { EventSession, FormStateEvent, Invitee } from 'modules/calendar/core/type';
import { getWeekDays } from 'modules/calendar/helpers';
import { deleteEvent, generateQRCode, getListEventSessions } from 'modules/calendar/services';
import useEventStore from 'modules/calendar/store/event';
import { ExerciseContentType } from 'modules/classes/core/enum';
import { Classes } from 'modules/classes/core/type';
import { Station } from 'modules/institution-info/core/type';
import { Learner } from 'modules/learners/core/type';
import { Trainer } from 'modules/trainers/core/type';

import { EventSessionDetail } from '../type';

import { addDays, addHours, format, isAfter, isBefore, parse, subDays, subHours } from 'date-fns';
import {
  getDateRange,
  getExtFileName,
  getFormattedDate,
  getNextRoundedHour,
  getReminderTime,
  getTimeLine,
  randomString,
  trimObject,
} from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useWidthAndHeightFile } from 'hooks/widthAndHeightFile';
import { isEmpty, isEqual, isNil, pickBy } from 'lodash';

import { ChallengeLevel, EventApplyOption, EventStatus, EventType } from '../enum';

export const useEventFormController = (
  classes: Classes[],
  view: View,
  eventSession: {
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
  },
  selectedDate: Date,
  drawer: {
    value: boolean;
    setValue: React.Dispatch<React.SetStateAction<boolean>>;
  },
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>,
) => {
  const [status, setStatus] = useState<EventStatus>(EventStatus.Pending);

  const isReminder = useBoolean(false);

  const [stations, setStations] = useState<Station[]>([]);

  const [contents, setContents] = useState<
    { id: string; classContentId: string; name: string; type: string }[]
  >([]);

  const [categories, setCategories] = useState<{ id: string; name: string; key: string }[]>([]);

  const [selectedInvitees, setSelectedInvitees] = useState<Invitee[]>([]);

  const setEvents = useEventStore((state) => state.setEvents);
  const insitution = useAuthStore(userProfileSelector).institution?.id || '';
  const eventSessionId = eventSession.value;

  const [inviteeState, setInviteeState] = useState({
    clientsPage: 1,
    trainersPage: 1,
    hasMoreClients: true,
    hasMoreTrainers: true,
    isLoadingClients: false,
    isLoadingTrainers: false,
  });

  const endRepeatValue = useString('after');

  const [formState, setFormState] = useState<FormStateEvent>(INITIAL_FORM_STATE(endRepeatValue));

  const [initialFormState, setInitialFormState] = useState<FormStateEvent>(
    INITIAL_FORM_STATE(endRepeatValue),
  );

  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const thumbnailUrl = useString('');

  const calendarKey = useNumber(0);

  const initialIsReminder = useRef(false);

  const [errorMessage, setErrorMessage] = useState<Record<string, string>>({
    selectedDate: '',
    recurringDate: '',
  });

  const { handleGetWidthAndHeightThumbnail, thumbnailHeight, thumbnailWidth } =
    useWidthAndHeightFile();

  useEffect(() => {
    getStations();
    getListCategory();
  }, []);

  useEffect(() => {
    if (formState.selectedClassId && Number(formState.eventType) === EventType.Class) {
      getListContent();
    }
  }, [formState.selectedClassId, formState.eventType]);

  useEffect(() => {
    if (eventSessionId) {
      getEventSessionDetail();
    }

    return () => {
      clearFormState();
      endRepeatValue.setValue('after');
      setSelectedInvitees([]);
    };
  }, [eventSessionId]);

  useEffect(() => {
    setErrorMessage({
      selectedDate: '',
      recurringDate: '',
    });
    setFormState((prev) => ({
      ...prev,
      startDate: eventSessionId ? initialFormState.startDate : selectedDate,
      endDate: eventSessionId ? initialFormState.endDate : selectedDate,
    }));
  }, [formState.eventType]);

  const getEventSessionDetail = async () => {
    const result: { data: EventSessionDetail } = await apiCall({
      url: `/calendars/events/sessions/${eventSessionId}`,
      method: 'GET',
      showError: true,
      isLoading: true,
    });

    if (result.data?.id) {
      const category = categories.find(
        (el) => String(el.id) === String(result.data.relatedSession?.category_id),
      );

      const level = levelOptions.find((el) => el.value === result.data.relatedSession?.level);

      let clientInvitees: Invitee[] = [];
      let trainerInvitees: Invitee[] = [];

      if (Number(result.data.event.type) !== EventType.Class) {
        clientInvitees = result.data.clients.map((client) => ({
          id: client.id,
          name: client.name,
          avatar: client.avatar,
          type: 'client',
          status:
            client.isAccepted === true ? 'yes' : client.isAccepted === false ? 'no' : 'awaiting',
        })) as Invitee[];

        trainerInvitees = result.data.trainers.map((trainer) => ({
          id: trainer.id,
          name: trainer.name,
          avatar: trainer.avatar,
          type: 'trainer',
          status:
            trainer.isAccepted === true ? 'yes' : trainer.isAccepted === false ? 'no' : 'awaiting',
        })) as Invitee[];
      }

      const updatedFormState = {
        eventType: result.data.event.type,
        selectedClassId: result.data.event.relationId,
        eventName: result.data.name,
        startDate: new Date(result.data.start),
        endDate: new Date(result.data.end),
        startTime: formatDate(new Date(result.data.start), 'HH:mm'),
        endTime: formatDate(new Date(result.data.end), 'HH:mm'),
        selectedCenterId: result.data.station?.id,
        selectedContentId: result.data.relatedSession?.classContentId,
        description: result.data.description,
        selectedLevel: {
          label: level?.label ?? '',
          value: String(level?.value) ?? '',
        },
        selectedCategory: {
          label: category?.name ?? '',
          value: category?.id ?? '',
        },
        thumbnail: result.data.relatedSession?.thumbnail ?? '',
        recurringRule: result.data.recurringRule?.frequency?.toLowerCase() || 'none',
        selectedInvitees:
          Number(result.data.event.type) !== EventType.Class
            ? [...clientInvitees, ...trainerInvitees].map((el) => el.id)
            : [],
        applyOption: result.data?.applyOption || EventApplyOption.ONLY_THIS_EVENT,
        count: result.data?.recurringRule?.count,
        recurringEnd: result.data?.recurringRule?.count
          ? new Date()
          : new Date(result.data?.recurringRule?.end),
        invitees:
          Number(result.data.event.type) !== EventType.Class
            ? {
                clients: {
                  items: clientInvitees,
                },
                trainers: {
                  items: trainerInvitees,
                },
              }
            : {
                clients: {
                  items: [],
                },
                trainers: {
                  items: [],
                },
              },
        qrKey: result.data.qrKey || '',
      };

      thumbnailUrl.setValue(result.data.relatedSession?.thumbnail ?? '');
      setFormState(updatedFormState);
      setInitialFormState(updatedFormState);
      setSelectedInvitees(
        Number(result.data.event.type) !== EventType.Class
          ? [...clientInvitees, ...trainerInvitees]
          : [],
      );
      setInviteeState({
        clientsPage: 1,
        trainersPage: 1,
        hasMoreClients: true,
        hasMoreTrainers: true,
        isLoadingClients: false,
        isLoadingTrainers: false,
      });
      setCurrentDate(new Date(result.data.start));

      if (!isNil(result.data.reminderTime)) {
        isReminder.setValue(true);
        initialIsReminder.current = true;
      }

      if (result.data.recurringRule?.count) {
        endRepeatValue.setValue('after');
      } else {
        endRepeatValue.setValue('on');
      }
    }
  };

  const getStations = async (institution_id?: string) => {
    const result: Station[] = await apiCall({
      url: '/stations',
      method: 'GET',
      data: {
        institutionId: institution_id,
      },
    });

    if (isEmpty(result)) {
      return;
    }

    setStations(result);
  };

  const getListContent = async () => {
    const result: any = await apiCall({
      url: `/classes/${formState.selectedClassId}/unschedule-lessons?type=${eventSessionId ? 'all' : 'unschedule'}`,
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        page: 1,
        limit: 9999,
      },
    });

    setContents(result ?? []);
  };

  const getListCategory = async () => {
    const result: { id: string; name: string; key: string }[] = await apiCall({
      url: '/configs?type=exercise_content_category',
      method: 'GET',
      isLoading: true,
      showError: true,
    });

    setCategories(result ?? []);
  };

  const fetchEventSessions = async () => {
    const res = await getListEventSessions(getDateRange(view, new Date(formState.startDate)));

    setEvents(res as EventSession[]);
  };

  const onChangeThumbnail = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('image')) {
      setThumbnail(file);
      thumbnailUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format image');
    }
  };

  // update state
  const updateFormState = <K extends keyof typeof formState>(
    key: K,
    value: (typeof formState)[K],
  ) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const clearFormState = () => {
    setFormState({
      eventType: EventType.Class,
      selectedClassId: undefined,
      eventName: '',
      startDate: new Date(),
      endDate: new Date(),
      startTime: getNextRoundedHour().startTime,
      endTime: getNextRoundedHour().endTime,
      selectedCenterId: undefined,
      selectedContentId: undefined,
      description: '',
      selectedLevel: undefined,
      selectedCategory: undefined,
      thumbnail: '',
      recurringRule: 'none',
      selectedInvitees: [],
      applyOption: EventApplyOption.ONLY_THIS_EVENT,
      invitees: {
        trainers: {
          items: [],
        },
        clients: {
          items: [],
        },
      },
      count: endRepeatValue.value === 'after' ? 2 : undefined,
      qrKey: '',
    });
  };

  const onChangeEventType = (newEventType: any) => {
    if (!newEventType) return;

    const newType = Number(newEventType.key);
    const currentType = Number(formState.eventType);

    const stateUpdate: Partial<typeof formState> = {
      eventType: newType,
    };

    if (currentType === EventType.Class && newType !== EventType.Class) {
      stateUpdate.selectedClassId = undefined;
      stateUpdate.selectedContentId = undefined;
      stateUpdate.selectedCenterId = undefined;
    }

    if (newType === EventType.Class && currentType !== EventType.Class) {
      stateUpdate.selectedCenterId = undefined;
    }

    updateFormState('eventType', newEventType.key);
    setFormState((prev) => ({ ...prev, ...stateUpdate }));
  };

  const onChangeRecurringRule = (newRecurringRule: any) => {
    if (!newRecurringRule) return;

    if (formState.recurringRule === 'none' && newRecurringRule.key !== 'none') {
      if (endRepeatValue.value === 'after') updateFormState('count', 2);

      if (endRepeatValue.value === 'on')
        updateFormState('recurringEnd', new Date(formState.startDate));
    }

    updateFormState('recurringRule', newRecurringRule.key);
  };

  const onChangeStatus = (newStatus: any) => {
    if (!newStatus) {
      return;
    }

    setStatus(newStatus.value);
  };

  const onChangeEventName = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFormState('eventName', event.target.value);
  };

  const handleChangeStartDate = (value: Date) => {
    updateFormState('startDate', value);

    if (formState.eventType === EventType.Gamification) {
      const maxEndDate = addDays(value, 14); // Maximum end date is 14 days from start date
      let newEndDate = formState.endDate;

      // If the new start date is greater than the current end date -> push the end date up 1 day later
      if (isAfter(value, formState.endDate)) newEndDate = addDays(value, 1);

      // If the new end date is greater than the maximum end date -> set the end date to the maximum end date
      if (isAfter(newEndDate, maxEndDate)) newEndDate = maxEndDate;

      updateFormState('endDate', newEndDate);
    }

    calendarKey.setValue(calendarKey.value + 1);
  };

  const handleChangeEndDate = (value: Date) => {
    if (formState.eventType === EventType.Gamification) {
      const maxEndDate = addDays(formState.startDate, 14); // Maximum end date is 14 days from start date
      let newStartDate = formState.startDate;

      // If the new end date is less than the current start date -> push the start date down 1 day earlier
      if (isBefore(value, formState.startDate)) newStartDate = subDays(value, 1);

      // If the new start date is greater than the maximum end date -> set the start date to the maximum end date
      if (isAfter(value, maxEndDate)) value = maxEndDate;

      updateFormState('startDate', newStartDate);
    }

    updateFormState('endDate', value);
    calendarKey.setValue(calendarKey.value + 1);
  };

  const handleChangeStartTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartTime = event.target.value;
    const newStartDate = parse(newStartTime, TIME_FORMAT, new Date());
    const endDate = parse(formState.endTime, TIME_FORMAT, new Date());

    if (newStartDate >= endDate) {
      const updatedEndDate = addHours(newStartDate, 1);

      updateFormState('endTime', format(updatedEndDate, TIME_FORMAT));
    }

    updateFormState('startTime', newStartTime);
  };

  const handleChangeEndTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndTime = event.target.value;
    const newEndDate = parse(newEndTime, TIME_FORMAT, new Date());
    const startDate = parse(formState.startTime, TIME_FORMAT, new Date());

    if (newEndDate <= startDate) {
      const updatedStartDate = subHours(newEndDate, 1);

      updateFormState('startTime', format(updatedStartDate, TIME_FORMAT));
    }

    updateFormState('endTime', newEndTime);
  };

  const onChangeClass = (newClass: any) => {
    if (!newClass) {
      return;
    }

    updateFormState('selectedClassId', newClass.value);
    updateFormState('selectedCenterId', newClass.stationId);
    updateFormState('selectedContentId', undefined);
  };

  const onChangeContent = (newContent: any) => {
    if (!newContent) {
      return;
    }

    updateFormState('selectedContentId', newContent.classContentId);

    if (newContent.subTitle?.toLowerCase() === ExerciseContentType.DIY)
      updateFormState('selectedCenterId', undefined);
  };

  const onChangeStation = (newStation: any) => {
    if (!newStation) {
      return;
    }

    updateFormState('selectedCenterId', newStation.value);
  };

  const handleChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormState('description', event.target.value);
  };

  const onChangeCategory = (newCategory: any) => {
    if (!newCategory) {
      return;
    }

    updateFormState('selectedCategory', newCategory);
  };

  const onChangeLevel = (newLevel: any) => {
    if (!newLevel) {
      return;
    }

    updateFormState('selectedLevel', newLevel);
  };

  const onChangeOccurrences = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFormState('count', Number(event.target.value));
  };

  const onChangeDate = (date: Date) => {
    setErrorMessage({
      ...errorMessage,
      recurringDate: isAfter(new Date(formState.startDate), new Date(date))
        ? 'Start date cannot be after recurring rule date'
        : '',
    });

    updateFormState('recurringEnd', date);
    calendarKey.setValue(calendarKey.value + 1);
  };

  const onChangeEndRepeatValue = (value: any) => {
    endRepeatValue.setValue(value);
  };

  const onChangeApplyOption = (value: EventApplyOption) => {
    updateFormState('applyOption', value);
  };

  const handleEndRepeatSubmit = () => {
    if (endRepeatValue.value === 'on') {
      updateFormState('recurringEnd', formState.recurringEnd);
      updateFormState('count', undefined);
    } else {
      updateFormState('recurringEnd', undefined);
      updateFormState('count', formState.count);
      setErrorMessage((prev) => ({
        ...prev,
        recurringDate: '',
      }));
    }

    closeModal(MODAL_KEY.END_REPEAT);
  };

  const handleGenerateQRCode = useCallback(async () => {
    const qrKey = await generateQRCode(eventSessionId);

    if (!isNil(qrKey)) updateFormState('qrKey', qrKey);
  }, [eventSessionId]);

  const onCreateEvent = async (cb?: () => void) => {
    const endDate =
      Number(formState.eventType) === EventType.Gamification
        ? new Date(formState.endDate)
        : new Date(formState.startDate);

    const resultUpload: {
      urlThumbnail: string;
      nameFileImage?: string;
    } | null = await handleUploadVideoAndThumbnail();

    if (!resultUpload) return;

    const request = {
      ...trimObject({
        name: formState.eventName,
        type: Number(formState.eventType),
        start: getTimeLine(new Date(formState.startDate), formState.startTime).toISOString(),
        end: getTimeLine(new Date(endDate), formState.endTime).toISOString(),
        description: formState.description,
        relationId:
          Number(formState.eventType) === EventType.Class
            ? formState.selectedClassId
            : formState.selectedCenterId,
        stationId: formState.selectedCenterId ?? null,
        recurringRule: {
          frequency: formState.recurringRule,
          weekDays: getWeekDays([formState.startDate]),
          interval: 1,
          nth: 1,
          count: formState.count,
        },
        recurringEnd: formState.recurringEnd,
        isRecurring: formState.recurringRule !== 'none' ? true : false,
        categoryId: Number(formState.selectedCategory?.value),
        level: Number(formState.selectedLevel?.value),
        reminderTime: isReminder.value
          ? getReminderTime(formState.startDate.toISOString())
          : undefined,
        applyOption: formState.applyOption,
        invitees: isEqual(formState.selectedInvitees, initialFormState.selectedInvitees)
          ? []
          : formState.selectedInvitees,
        thumbnail: resultUpload.nameFileImage || formState.thumbnail,
        relationContentId: formState.selectedContentId,
      }),
    };

    if (resultUpload.urlThumbnail && thumbnailHeight.value && thumbnailWidth.value) {
      request.thumbnailHeight = thumbnailHeight.value;
      request.thumbnailWidth = thumbnailWidth.value;
    }

    const result: { statusCode: number } = await apiCall({
      url: eventSessionId ? `/calendars/events/sessions/${eventSessionId}` : '/calendars/events',
      method: eventSessionId ? 'PATCH' : 'POST',
      data: request,
      isLoading: true,
      showError: true,
    });

    if (result.statusCode === HttpStatusCode.Ok) {
      showToastMessage('success', `${eventSessionId ? 'Saved.' : 'Created event.'} successfully`);

      await fetchEventSessions();

      setCurrentDate(new Date(formState.startDate));

      cb?.();
      clearFormState();
    }
  };

  const eventOptions = [
    { key: String(EventType.Class), label: 'Class lesson' },
    { key: String(EventType.Gamification), label: 'Challenge' },
    { key: String(EventType.Other), label: 'Other event' },
  ];

  const startDateInfo = getFormattedDate(formState.startDate.toISOString());

  const recurringRuleOptions = [
    { key: 'none', label: 'Does not repeat' },
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: `Weekly on ${startDateInfo.date}` },
    { key: 'monthly', label: `Monthly on ${startDateInfo.day}` },
    { key: 'yearly', label: `Annually on ${startDateInfo.month} ${startDateInfo.day}` },
  ];

  const statusOptions = [
    { key: EventStatus.Approved, label: 'Approved' },
    { key: EventStatus.Pending, label: 'Pending' },
    { key: EventStatus.Declined, label: 'Declined' },
  ];

  const selectedClass = classes.find((el) => el.id === formState.selectedClassId);

  const selectedContent = contents.find((el) => el.classContentId === formState.selectedContentId);

  const selectedStation = stations.find((el) => el.id === formState.selectedCenterId);

  const categoyOptions = categories
    .map((el) => ({
      label: el.name,
      value: el.id,
    }))
    .filter((el) => el.label.includes('Strengthening') || el.label.includes('Cardio'));

  const levelOptions = [
    { label: 'Level 1', value: ChallengeLevel.Level1 },
    { label: 'Level 2', value: ChallengeLevel.Level2 },
    { label: 'Level 3', value: ChallengeLevel.Level3 },
  ];

  const initializeWithDate = useCallback(
    (date: Date) =>
      setFormState((prev) => ({
        ...prev,
        startDate: date,
        endDate: date,
      })),
    [],
  );

  useEffect(() => {
    if (selectedDate && !eventSessionId) initializeWithDate(selectedDate);
  }, [selectedDate, initializeWithDate]);

  const disabledSubmitBtn =
    (isEmpty(eventSessionId) &&
      (isEmpty(formState.eventName) ||
        // isEmpty(formState.invitees.trainers.items) ||
        // isEmpty(formState.invitees.clients.items)
        // ||
        (Number(formState.eventType) !== EventType.Gamification &&
          (isEmpty(formState.startTime) || isEmpty(formState.endTime))) ||
        (Number(formState.eventType) === EventType.Class &&
          (isNil(formState.selectedClassId) ||
            isNil(formState.selectedContentId) ||
            (selectedContent?.type?.toLowerCase() !== ExerciseContentType.DIY &&
              isNil(formState.selectedCenterId)))) ||
        (Number(formState.eventType) === EventType.Gamification &&
          (isEmpty(formState.selectedCategory) ||
            isNil(formState.selectedLevel) ||
            isEmpty(thumbnailUrl.value))) ||
        (Number(formState.eventType) === EventType.Other && isNil(formState.selectedCenterId)))) ||
    (!isEmpty(eventSessionId) &&
      isEqual(
        {
          ...formState,
          selectedLevel: {
            label: formState.selectedLevel?.label,
            value: Number(formState.selectedLevel?.value),
          },
        },
        {
          ...initialFormState,
          selectedLevel: {
            label: initialFormState.selectedLevel?.label,
            value: Number(initialFormState.selectedLevel?.value),
          },
        },
      ) &&
      isEqual(initialIsReminder.current, isReminder.value)) ||
    !isEmpty(pickBy(errorMessage, (value) => !isEmpty(value)));

  const fetchClassDetail = async () => {
    const result: Classes = await apiCall({
      url: `/classes/${selectedClass?.id}`,
      method: 'GET',
      showError: true,
    });

    const clients = result.learners?.map((learner) => ({
      id: learner.id,
      name: learner.name,
      avatar: learner.physicalProfile?.avatar,
      type: 'client',
    })) as Invitee[];

    const trainers = result.trainers?.map((trainer) => ({
      id: trainer.id,
      name: trainer.name,
      avatar: trainer.physicalProfile?.avatar,
      type: 'trainer',
    })) as Invitee[];

    setFormState((prev) => ({
      ...prev,
      selectedInvitees: [...clients, ...trainers].map((invitee) => invitee.id),
    }));
    setInitialFormState((prev) => ({
      ...prev,
      selectedInvitees: [...clients, ...trainers].map((invitee) => invitee.id),
    }));
    setSelectedInvitees([...clients, ...trainers]);
  };

  useEffect(() => {
    if (!isNil(selectedClass) && EventType.Class === formState.eventType) fetchClassDetail();
  }, [selectedClass, formState.eventType, eventSessionId]);

  useEffect(() => {
    if (!isEmpty(selectedInvitees)) {
      updateFormState(
        'selectedInvitees',
        selectedInvitees.map((invitee) => invitee.id),
      );

      return;
    }

    updateFormState('selectedInvitees', []);
  }, [selectedInvitees]);

  useEffect(() => {
    if (!eventSessionId) {
      setInviteeState({
        clientsPage: 1,
        trainersPage: 1,
        hasMoreClients: true,
        hasMoreTrainers: true,
        isLoadingClients: false,
        isLoadingTrainers: false,
      });

      setFormState((prev) => ({
        ...prev,
        invitees: {
          trainers: {
            items: [],
          },
          clients: {
            items: [],
          },
        },
      }));

      setSelectedInvitees([]);
    }
  }, [formState.eventType]);

  useEffect(() => {
    if (!drawer.value) {
      clearFormState();
      setSelectedInvitees([]);
      eventSession.setValue('');
      thumbnailUrl.setValue('');
      setErrorMessage({
        selectedDate: '',
        recurringDate: '',
      });
      isReminder.setValue(false);
      initialIsReminder.current = false;
      endRepeatValue.setValue('after');
    }
  }, [drawer]);

  const handleSelectedInviteesChange = (invitees: Invitee[]) => setSelectedInvitees(invitees);

  const handleRemoveThumbnail = () => {
    setThumbnail(null);
    setFormState((prev) => ({
      ...prev,
      thumbnail: '',
    }));

    if (initialFormState.thumbnail?.includes('thumbnail-default')) {
      thumbnailUrl.setValue(initialFormState.thumbnail);

      return;
    }

    thumbnailUrl.setValue('');
  };

  const getListClients = useCallback(async () => {
    if (!inviteeState.hasMoreClients || inviteeState.isLoadingClients) return;

    setInviteeState((prev) => ({ ...prev, isLoadingClients: true }));

    try {
      const result: any = await apiCall<{ items: Learner[] }>({
        url: `/users?page=${inviteeState.clientsPage}&limit=${ITEMS_PER_PAGE}&roleKey=${RoleKey.LEARNER}&institutionId=${insitution}`,
        method: 'GET',
        showError: true,
      });

      const clients = result.items.map((client: Learner) => ({
        id: client.id,
        name: client.name,
        avatar: client.physicalProfile?.avatar,
        type: 'client',
        status: 'awaiting',
      }));

      setFormState((prev) => ({
        ...prev,
        invitees: {
          ...prev.invitees,
          clients: {
            ...prev.invitees.clients,
            items:
              inviteeState.clientsPage === 1
                ? clients
                : [...prev.invitees.clients.items, ...clients],
          },
        },
      }));

      setInviteeState((prev) => ({
        ...prev,
        hasMoreClients: clients.length === ITEMS_PER_PAGE,
        clientsPage: prev.clientsPage + 1,
      }));
    } finally {
      setInviteeState((prev) => ({ ...prev, isLoadingClients: false }));
    }
  }, [
    inviteeState.hasMoreClients,
    inviteeState.isLoadingClients,
    inviteeState.clientsPage,
    insitution,
  ]);

  const getListTrainers = useCallback(async () => {
    if (!inviteeState.hasMoreTrainers || inviteeState.isLoadingTrainers) return;

    setInviteeState((prev) => ({ ...prev, isLoadingTrainers: true }));

    try {
      const result: any = await apiCall<Trainer[]>({
        url: `/users?page=${inviteeState.trainersPage}&limit=${ITEMS_PER_PAGE}&roleKey=${RoleKey.TRAINER_INSTITUTION}&institutionId=${insitution}`,
        method: 'GET',
        showError: true,
      });

      const trainers = result.items.map((trainer: Trainer) => ({
        id: trainer.id,
        name: trainer.name,
        avatar: trainer.physicalProfile?.avatar,
        type: 'trainer',
        status: 'awaiting',
      }));

      setFormState((prev) => ({
        ...prev,
        invitees: {
          ...prev.invitees,
          trainers: {
            ...prev.invitees.trainers,
            items:
              inviteeState.trainersPage === 1
                ? trainers
                : [...prev.invitees.trainers.items, ...trainers],
          },
        },
      }));

      setInviteeState((prev) => ({
        ...prev,
        hasMoreTrainers: trainers.length === ITEMS_PER_PAGE,
        trainersPage: prev.trainersPage + 1,
      }));
    } finally {
      setInviteeState((prev) => ({ ...prev, isLoadingTrainers: false }));
    }
  }, [
    inviteeState.hasMoreTrainers,
    inviteeState.isLoadingTrainers,
    inviteeState.trainersPage,
    insitution,
  ]);

  const handleTabChange = useCallback(
    (tab: 'clients' | 'trainers') => {
      if (Number(formState.eventType) !== EventType.Class) {
        if (tab === 'clients' && inviteeState.clientsPage === 1) {
          getListClients();
        }

        if (tab === 'trainers' && inviteeState.trainersPage === 1) {
          getListTrainers();
        }
      }
    },
    [
      formState.eventType,
      inviteeState.clientsPage,
      inviteeState.trainersPage,
      inviteeState.isLoadingClients,
      inviteeState.isLoadingTrainers,
      getListClients,
      getListTrainers,
    ],
  );

  const handleDropdownOpen = useCallback(() => {
    if (formState.eventType !== EventType.Class) {
      getListClients();
    }
  }, [formState.eventType, getListClients]);

  const handleCloseModal = (key: string) => () => {
    closeModal(key);
  };

  const handleDeleteEvent = useCallback(async () => {
    const res = await deleteEvent(eventSessionId, formState.applyOption);

    if (!res) return;

    if (formState.recurringRule !== 'none') {
      closeModal(MODAL_KEY.DELETE_RECURRING_EVENT);
    }

    if (formState.recurringRule === 'none') {
      closeModal(MODAL_KEY.DELETE_RECURRING_NOT_UPDATED_EVENT);
    }

    await fetchEventSessions();
    drawer.setValue(false);
    showToastMessage('success', 'Deleted');
  }, [eventSessionId, formState.applyOption, formState.recurringRule]);

  const handleClickDelete = () => {
    if (formState.recurringRule !== 'none') {
      openModal(MODAL_KEY.DELETE_RECURRING_EVENT);

      return;
    }

    openModal(MODAL_KEY.DELETE_RECURRING_NOT_UPDATED_EVENT);
    updateFormState('applyOption', EventApplyOption.THIS_AND_FUTURE_EVENTS);
  };

  const handleClickSaveBtn = () => {
    if (Number(formState.eventType) === EventType.Other) {
      const currentRecurringRule = formState.recurringRule;

      if (
        !isEqual(currentRecurringRule, initialFormState.recurringRule) &&
        formState.recurringRule !== 'none'
      ) {
        openModal(MODAL_KEY.EDIT_RECURRING_EVENT);

        return;
      }

      if (
        isEqual(currentRecurringRule, initialFormState.recurringRule) &&
        formState.recurringRule !== 'none'
      ) {
        openModal(MODAL_KEY.EDIT_RECURRING_NOT_UPDATED_EVENT);
        updateFormState('applyOption', EventApplyOption.THIS_AND_FUTURE_EVENTS);

        return;
      }
    }

    onCreateEvent(() => {
      drawer.setValue(false);
    });
  };

  const handleCreateEvent = () =>
    onCreateEvent(() => {
      drawer.setValue(false);
    });

  const handleSubmitEditRecurringEvent = (key: string) => () => {
    onCreateEvent(() => {
      drawer.setValue(false);
      closeModal(key);
    });
  };

  const handleUploadVideoAndThumbnail = async () => {
    let urlThumbnail = thumbnailUrl.value;
    let nameFileImage = formState.thumbnail;

    try {
      if (thumbnail) {
        const thumbnailExt = getExtFileName(thumbnail.name);

        nameFileImage = `thumbnail-exercise-${randomString(4)}-${Date.now()}${thumbnailExt}`;

        urlThumbnail = await UploadFile(thumbnail, nameFileImage);
      }

      return {
        urlThumbnail,
        nameFileImage,
      };
    } catch (error) {
      console.log(error);

      return null;
    }
  };

  const handleInviteeScroll = useCallback(() => {
    if (Number(formState.eventType) !== EventType.Class) return true;

    return false;
  }, [formState.eventType]);

  return {
    formState,
    onChangeEventType,
    eventOptions,
    recurringRuleOptions,
    onChangeRecurringRule,
    status,
    onChangeStatus,
    statusOptions,
    isReminder,
    classes,
    onChangeClass,
    selectedClass,
    onChangeEventName,
    handleChangeStartDate,
    handleChangeStartTime,
    handleChangeEndTime,
    contents,
    onChangeContent,
    handleChangeDescription,
    selectedContent,
    stations,
    onChangeStation,
    selectedStation,
    onCreateEvent,
    handleChangeEndDate,
    categoyOptions,
    onChangeCategory,
    levelOptions,
    onChangeLevel,
    onChangeThumbnail,
    thumbnailUrl,
    initializeWithDate,
    disabledSubmitBtn,
    selectedInvitees,
    setSelectedInvitees: handleSelectedInviteesChange,
    handleRemoveThumbnail,
    inviteeState,
    getListClients,
    getListTrainers,
    handleTabChange,
    onChangeOccurrences,
    onChangeDate,
    onChangeEndRepeatValue,
    endRepeatValue,
    handleEndRepeatSubmit,
    onChangeApplyOption,
    handleCloseModal,
    handleClickSaveBtn,
    handleCreateEvent,
    handleSubmitEditRecurringEvent,
    handleGetWidthAndHeightThumbnail,
    handleDropdownOpen,
    handleInviteeScroll,
    handleGenerateQRCode,
    handleClickDelete,
    handleDeleteEvent,
    errorMessage,
    calendarKey,
  };
};
